import React from 'react';
import { Badge } from '../ui/badge';
import { StatusBadge } from '../ui/status-badge';
import { Button } from '../ui/button';
import {
    Users,
    Truck,
    ChevronRight,
    Package
} from 'lucide-react';
import { Order, OrderStatus } from '../../lib/types';
import {
    formatCurrency,
    formatDateShort,
    formatTime,
    getOrderStatusBadgeVariant,
    formatOrderStatus,
} from '../../lib/utils';

interface MobileOrderCardProps {
    order: Order;
    onViewDetails: (orderId: string) => void;
}

const MobileOrderCard: React.FC<MobileOrderCardProps> = ({ order, onViewDetails }) => {
    // Get assignment status display
    const getAssignmentStatus = () => {
        const hasSupplier = order.assignedSupplier || order.supplier ||
            (order.items && order.items.some(item => item.assignedSupplier || item.supplier || item.supplierName));

        const hasTransporter = order.transporter;

        const needsTransporter = ['confirmed', 'processing', 'ready_for_pickup'].includes(order.status);

        return {
            supplier: hasSupplier ? 'assigned' : 'pending',
            transporter: hasTransporter ? 'assigned' : (needsTransporter ? 'pending' : 'not_needed')
        };
    };

    const assignment = getAssignmentStatus();

    return (
        <div
            className="mobile-data-card cursor-pointer"
            onClick={() => onViewDetails(order._id)}
        >
            {/* Header */}
            <div className="mobile-data-card-header">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Package className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                        <span className="font-semibold text-primary text-sm">
                            {order.orderNumber}
                        </span>
                        <div className="text-xs text-muted-foreground">
                            {formatDateShort(order.createdAt)} • {formatTime(order.createdAt)}
                        </div>
                    </div>
                </div>
                <StatusBadge status={order.status} />
            </div>

            {/* Content */}
            <div className="mobile-data-card-content">
                <div className="mobile-data-card-row">
                    <span className="mobile-data-card-label">Customer</span>
                    <span className="mobile-data-card-value truncate max-w-[180px]">
                        {order.trader?.firstName ? `${order.trader.firstName} ${order.trader.lastName || ''}` : order.trader?.email || 'N/A'}
                    </span>
                </div>

                <div className="mobile-data-card-row">
                    <span className="mobile-data-card-label">Items</span>
                    <Badge variant="outline" className="font-medium text-xs">
                        {order.itemsCount ?? order.items?.length ?? 0} items
                    </Badge>
                </div>

                <div className="mobile-data-card-row">
                    <span className="mobile-data-card-label">Total</span>
                    <span className="font-bold text-base text-primary">
                        {formatCurrency(order.totalAmount)}
                    </span>
                </div>
            </div>

            {/* Footer with assignments */}
            <div className="mobile-data-card-actions justify-between">
                <div className="flex gap-2">
                    <div className="flex items-center text-xs">
                        <Users className="h-3 w-3 mr-1 text-muted-foreground" />
                        <Badge
                            variant={assignment.supplier === 'assigned' ? 'success' : 'warning'}
                            className="text-[10px] px-1.5 py-0"
                        >
                            {assignment.supplier === 'assigned' ? '✓' : '!'}
                        </Badge>
                    </div>
                    {assignment.transporter !== 'not_needed' && (
                        <div className="flex items-center text-xs">
                            <Truck className="h-3 w-3 mr-1 text-muted-foreground" />
                            <Badge
                                variant={assignment.transporter === 'assigned' ? 'success' : 'warning'}
                                className="text-[10px] px-1.5 py-0"
                            >
                                {assignment.transporter === 'assigned' ? '✓' : '!'}
                            </Badge>
                        </div>
                    )}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
        </div>
    );
};

export default MobileOrderCard;
